import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useScrollReveal } from './useScrollReveal';
import { fetchBillingPackages, splitBillingPackages, getBillingOfferTitle, getBillingOfferPrice, getPerCreditCopy, getSavingsCopy, getOfferDescription, getOfferFeatures } from '../../utils/billing';

export default function PricingSection({ onSignUp, isAuthenticated }) {
  const reveal = useScrollReveal();
  const [loading, setLoading] = useState(true);
  const fallbackPackages = useMemo(() => ([
    { id: 'credit_single', kind: 'credit_pack', mode: 'payment', credits: 1, amount: 500, per_credit_amount: 500, currency: 'usd', name: '1 credit', description: 'One fresh theme set with the matching event plan included.' },
    { id: 'credit_5', kind: 'credit_pack', mode: 'payment', credits: 5, amount: 2250, per_credit_amount: 450, savings_amount: 250, savings_percent: 10, currency: 'usd', name: '5 credits', description: 'A lower per-credit rate for multiple events or regenerations.' },
    { id: 'credit_10', kind: 'credit_pack', mode: 'payment', credits: 10, amount: 4000, per_credit_amount: 400, savings_amount: 1000, savings_percent: 20, currency: 'usd', name: '10 credits', description: 'Best value for frequent planning and repeat events.' },
    { id: 'plus_monthly', kind: 'subscription', mode: 'subscription', credits: 2, amount: 900, per_credit_amount: 450, currency: 'usd', interval: 'month', name: 'Plus monthly', description: '2 planning credits every month with rollover.' },
    { id: 'plus_annual', kind: 'subscription', mode: 'subscription', credits: 24, amount: 9000, per_credit_amount: 375, savings_amount: 1800, savings_percent: 16.7, currency: 'usd', interval: 'year', name: 'Plus annual', description: '24 planning credits per year, delivered monthly with rollover.' }
  ]), []);
  const [packages, setPackages] = useState(fallbackPackages);
  const [country, setCountry] = useState('US');
  const [pricingTab, setPricingTab] = useState('packs');

  const mergeWithFallbackPackages = useCallback((apiPackages = []) => {
    const apiById = new Map((apiPackages || []).filter(Boolean).map((pkg) => [pkg.id, pkg]));
    const merged = fallbackPackages.map((fallback) => ({
      ...fallback,
      ...(apiById.get(fallback.id) || {})
    }));
    (apiPackages || []).forEach((pkg) => {
      if (pkg?.id && !fallbackPackages.some((fallback) => fallback.id === pkg.id)) {
        merged.push(pkg);
      }
    });
    return merged;
  }, [fallbackPackages]);

  useEffect(() => {
    let mounted = true;

    const loadPricing = async () => {
      try {
        const data = await fetchBillingPackages('US');

        if (!mounted) return;
        setCountry('US');
        setPackages(data.packages?.length ? mergeWithFallbackPackages(data.packages) : fallbackPackages);
      } catch (err) {
        console.error('Failed to load pricing:', err);
        if (mounted) {
          setCountry('US');
          setPackages(fallbackPackages);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadPricing();
    return () => { mounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { creditPacks, subscriptions } = useMemo(() => splitBillingPackages(packages), [packages]);
  const visibleOffers = pricingTab === 'packs'
    ? (creditPacks.length ? creditPacks : splitBillingPackages(fallbackPackages).creditPacks)
    : (subscriptions.length ? subscriptions : splitBillingPackages(fallbackPackages).subscriptions);

  const renderOffer = (pkg) => (
    <div
      key={pkg.id}
      className="sr-item sr-visible group bg-white rounded-2xl p-5 sm:p-6 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-lg border-[1.5px] border-[var(--brand-border)] hover:border-stone-400 relative overflow-hidden h-full"
    >
      {/* 1. Header Slot - flex-1 enables dynamic height and perfect bottom alignment */}
      <div className="flex-1 mb-6">
        <h3 className="text-xl font-extrabold text-gray-950 mb-3">{getBillingOfferTitle(pkg)}</h3>
        <ul className="space-y-3 text-sm text-gray-600">
          {getOfferFeatures(pkg).map((feature, i) => (
            <li key={i} className="flex items-start gap-2.5">
              <svg className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="leading-snug">{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* 2. Price Slot */}
      <div className="mb-4">
        <div className="text-3xl font-extrabold text-gray-950">
          {getBillingOfferPrice(pkg, country)}
        </div>
        <div className="mt-1 text-sm font-medium text-gray-500">
          {getPerCreditCopy(pkg, country) || '\u00A0'}
        </div>
      </div>

      {/* 3. Savings Slot */}
      <div className="mb-6 flex items-center min-h-[26px]">
        {getSavingsCopy(pkg, country) ? (
          <div className="inline-flex rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700 ring-1 ring-emerald-100">
            {getSavingsCopy(pkg, country)}
          </div>
        ) : (
          <div className="h-[26px]" /> // Spacer to preserve vertical alignment
        )}
      </div>

      {/* 4. Action Slot */}
      <div className="mt-auto">
        <button
          onClick={onSignUp}
          className="w-full py-3 px-4 rounded-xl font-bold text-sm transition-all duration-200"
          style={{
            backgroundColor: 'var(--brand-violet)',
            color: 'white',
            boxShadow: '0 2px 8px rgba(24,24,27,0.12)'
          }}
          onMouseEnter={(e) => (e.target.style.transform = 'scale(1.02)')}
          onMouseLeave={(e) => (e.target.style.transform = 'scale(1)')}
        >
          {pkg.kind === 'subscription' ? 'Subscribe Now' : 'Buy Credits'}
        </button>
      </div>
    </div>
  );

  return (
    <section id="pricing" className="py-20 sm:py-28 px-4 sm:px-6 bg-white border-t border-[var(--brand-border)] overflow-hidden w-full max-w-full">
      <div className="max-w-6xl mx-auto w-full">
        {/* Header */}
        <div className="text-center mb-14 sr-item" ref={reveal}>
          <p className="text-sm font-semibold uppercase tracking-widest mb-3 text-zinc-900">
            Simple Pricing
          </p>
          <h2
            className="font-extrabold leading-tight mb-4"
            style={{ fontSize: 'clamp(1.8rem, 4vw, 2.75rem)', color: 'var(--brand-charcoal)', letterSpacing: '-0.025em' }}
          >
            Pay for what you need.
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            1 credit creates one fresh AI-generated planning result. Unused credits stay in your account for future plans.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-zinc-900 border-t-transparent"></div>
          </div>
        ) : (
          <div className="sr-stagger" ref={reveal}>
            <div className="mb-8 flex justify-center">
              <div className="inline-flex rounded-full border border-gray-200 bg-gray-50 p-1">
                <button
                  type="button"
                  onClick={() => setPricingTab('packs')}
                  className={`rounded-full px-5 py-2 text-sm font-bold transition ${pricingTab === 'packs' ? 'bg-white text-gray-950 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
                >
                  Credit packs
                </button>
                <button
                  type="button"
                  onClick={() => setPricingTab('subscriptions')}
                  className={`rounded-full px-5 py-2 text-sm font-bold transition ${pricingTab === 'subscriptions' ? 'bg-white text-gray-950 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
                >
                  Subscriptions
                </button>
              </div>
            </div>

            <div className="flex sm:flex-wrap justify-start sm:justify-center gap-4 sm:gap-5 overflow-x-auto sm:overflow-x-visible pt-2 pb-6 sm:pb-0 px-4 sm:px-0 -mx-4 sm:mx-0 snap-x cmp-scrollbar-hide">
              {visibleOffers.map((pkg) => (
                <div key={pkg.id} className="w-[280px] sm:w-[calc(33.333%-1rem)] md:w-[280px] lg:w-[320px] flex-shrink-0 snap-center ml-4 first:ml-4 last:mr-4 sm:ml-0">
                  {renderOffer(pkg)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
